// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import type { RouterHistory, Location } from 'react-router-dom';
import type { CmsSubscribe } from 'api/cms';
import { postJson } from 'utils/http';
import RichError from 'utils/RichError';
import trackError from 'utils/trackError';
import { showModal as showModalRaw } from '../redux/main/actions/modal';
import useTemporaryStorage from '../utils/useTemporaryStorage';
import { isUrlWithProtocol, getParam, pathWithParams } from '../utils/url';

const { useState, useEffect, useCallback } = React;

type OwnProps = {
  blockId: string,
  index: number,
  redirectUrl?: string,
  render: ({
    subscribe: () => void,
    isSubscribed: boolean
  }) => React.Node,
  history: RouterHistory,
  location: Location
};

type Props = {
  ...$Exact<OwnProps>,
  currentUser: Object,
  showModal: typeof showModalRaw,
  tmpId: ?string
};

const CmsSubscribeContainer = (props: Props) => {
  const [disabled, setDisabled] = useState(false);
  const [tmpId, setTmpId] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const [
    saveTemporaryStorage,
    temporaryStorage
  ] = useTemporaryStorage<CmsSubscribe>(props.tmpId);

  const subscribe = useCallback(
    () => {
      const { currentUser } = props;

      const showEmailSignupModal = (tmpIdToUse: string) => {
        const redirectTo = pathWithParams(
          `${window.location.pathname}${window.location.search}`,
          { tmpId: tmpIdToUse }
        );

        props.showModal('EMAIL_SIGNUP', {
          title: getText('Create an account on Idealist in order to subscribe'),
          redirectTo
        });
      };

      const loggedInVerifiedWithInfo = !!(
        currentUser &&
        currentUser.email !== null &&
        currentUser.emailVerified &&
        currentUser.firstName &&
        currentUser.lastName
      );

      const redirectUrl = temporaryStorage.redirectUrl || props.redirectUrl;

      if (loggedInVerifiedWithInfo) {
        const postBaseUrl = '/data/website/button-link-campaign-signup/';
        postJson(postBaseUrl + props.blockId, { index: props.index })
          .then(() => {
            if (typeof redirectUrl === 'string') {
              if (isUrlWithProtocol(redirectUrl)) {
                window.location.href = redirectUrl;
                return;
              }
              props.history.push(
                redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`
              );
            } else {
              setIsSubscribed(true);
            }
          })
          .catch(e => {
            trackError(
              new RichError('Error subscribing in CMS block', JSON.stringify(e))
            );
          });
      } else {
        if (!disabled && !tmpId) {
          setDisabled(true);
          saveTemporaryStorage({
            CMS_SUBSCRIBE: {
              blockId: props.blockId,
              index: props.index,
              redirectUrl: props.redirectUrl
            }
          }).then(createdTmpId => {
            setDisabled(false);
            setTmpId(createdTmpId);
            return showEmailSignupModal(createdTmpId);
          });
        }

        showEmailSignupModal(tmpId);
      }
    },
    [disabled, props, saveTemporaryStorage, temporaryStorage, tmpId]
  );

  useEffect(
    () => {
      if (
        temporaryStorage.blockId === props.blockId &&
        temporaryStorage.index === props.index &&
        props.currentUser.emailVerified
      ) {
        subscribe();
      }
    },
    [
      props.blockId,
      props.currentUser.emailVerified,
      props.index,
      subscribe,
      temporaryStorage
    ]
  );
  return props.render({
    subscribe: subscribe.bind(this),
    isSubscribed
  });
};

const mapStateToProps = ({ currentUser }, { location }) => ({
  currentUser,
  tmpId: getParam(location, 'tmpId')
});

export default withRouter(
  connect(
    mapStateToProps,
    { showModal: showModalRaw }
  )(CmsSubscribeContainer)
);
